import {Config} from "../Config";
import {AuthResponse, refresh_token} from "./Auth";

const config = new Config();

export type PickResponse = {
    succeeded: boolean,
    statusCode: number,
    message: string,
    data?: any,
}

function extractErrorMessage(data: any): string {
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
        if (typeof data.detail === 'string') return data.detail;
        if (typeof data.message === 'string') return data.message;
        if (typeof data.error === 'string') return data.error;
        const firstKey = Object.keys(data)[0];
        if (firstKey) {
            const firstVal = data[firstKey];
            if (typeof firstVal === 'string') return firstVal;
            if (Array.isArray(firstVal) && typeof firstVal[0] === 'string') return firstVal[0];
        }
        try { return JSON.stringify(data); } catch { return "Unknown error"; }
    }
    return "Unknown error";
}

export async function copyPicks(access: string, refresh: string, fromLeagueId: number, toLeagueId: number, eventId: string): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.pick_copy, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify({
                    'event': eventId,
                    'from_league': fromLeagueId,
                    'to_league': toLeagueId
                })
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Picks Copied Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await copyPicks(access, refresh, fromLeagueId, toLeagueId, eventId);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: "Failed to copy picks", data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function copyPicksProMod(access: string, refresh: string, fromLeagueId: number, toLeagueId: number, eventId: string): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.pick_copy_pro_mod, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify({
                    'event': eventId,
                    'from_league': fromLeagueId,
                    'to_league': toLeagueId
                })
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Pro Mod Picks Copied Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await copyPicksProMod(access, refresh, fromLeagueId, toLeagueId, eventId);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: "Failed to copy pro mod picks", data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function submitPicks(access: string, refresh: string, picks: any): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.pick_bulk, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify(picks)
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Picks Saved Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await submitPicks(access, refresh, picks);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: data, data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function submitPicksTopMoments(access: string, refresh: string, picks: any): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.pick_bulk_top_moments, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify(picks)
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Picks Saved Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await submitPicks(access, refresh, picks);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: data, data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function submitPicksProMod(access: string, refresh: string, picks: any): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.pick_bulk_pro_mod, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify(picks)
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Picks Saved Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await submitPicksProMod(access, refresh, picks);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: data, data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function submitPicksFourWide(access: string, refresh: string, picks: any): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.four_wide_pick_bulk, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify(picks)
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Picks Saved Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await submitPicksFourWide(access, refresh, picks);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: data, data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function submitPicksFourWideProMod(access: string, refresh: string, picks: any): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.four_wide_pick_bulk_pro_mod, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify(picks)
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Picks Saved Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await submitPicksFourWideProMod(access, refresh, picks);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: data, data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function submitPicksAdmin(access: string, refresh: string, picks: any): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.pick_bulk_admin, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify(picks)
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Picks Saved Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await submitPicks(access, refresh, picks);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: extractErrorMessage(data), data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function submitPicksAdminProMod(access: string, refresh: string, picks: any): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.pick_bulk_admin_pro_mod, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify(picks)
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Picks Saved Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await submitPicksAdminProMod(access, refresh, picks);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: extractErrorMessage(data), data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function submitPicksAdminFourWide(access: string, refresh: string, picks: any): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.four_wide_pick_bulk_admin, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify(picks)
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Picks Saved Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await submitPicksFourWide(access, refresh, picks);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: extractErrorMessage(data), data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function submitPicksAdminFourWideProMod(access: string, refresh: string, picks: any): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.four_wide_pick_bulk_admin_pro_mod, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                },
                body: JSON.stringify(picks)
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Picks Saved Successfully", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await submitPicksAdminFourWideProMod(access, refresh, picks);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: extractErrorMessage(data), data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}

export async function pickStats(access: string, refresh: string): Promise<PickResponse> {
    if (window.location.pathname === '/error') {
        return {succeeded: false, statusCode: 400, message: "Servers are down, please come back later"};
    }
    try {
        if (access === null) {
            return {succeeded: false, statusCode: 401, message: "Access token is empty"};
        } else {
            let response: Response = await fetch(config.backendURL + config.endpoints.pick_stats, {
                method: 'GET',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'JWT ' + access,
                }
            });
            let data = await response.json();
            let responseStatus: number = response.status;
            if (responseStatus < 400) {
                return {succeeded: true, statusCode: 200, message: "Successfully pulled user's pick stats", data: data};
            }
            else if(responseStatus === 401){
                let refresh_validation: AuthResponse = await refresh_token(refresh);
                if (refresh_validation.succeeded) {
                    return await pickStats(access, refresh);
                } else {
                    return {succeeded: false, statusCode: 401, message: "Access token is invalid"};
                }
            }
            else{
                return {succeeded: false, statusCode: 400, message: "Failed to pull user's pick stats", data: data};
            }
        }
    } catch (e) {
        if(e instanceof TypeError && e.message === "Failed to fetch"){
            window.location.href = '/error';
        }
        return {succeeded: false, statusCode: 500, message: "Something went wrong on our end"};
    }
}